import { BadgeCheck, Building2, Check, CircleDot, User } from 'lucide-react';
import type { SigningParty } from '../lib/types';

/**
 * Who has signed, who is signing, and who is waiting.
 *
 * ## Why this is a component and not a status badge
 *
 * "Awaiting employee" tells somebody the state and not the story. A person
 * opening a contract wants three things at a glance: has anybody signed this,
 * is it my turn, and if not, whose. A single badge answers the first and buries
 * the other two — which is why every product that does this well, SignEasy
 * included, draws the sequence rather than naming a state.
 *
 * Drawing it also makes the *order* legible, which is the point of having one.
 * Somebody who can see that the company signs first understands why the button
 * is not there yet, instead of concluding the page is broken.
 *
 * ## Why the turn is passed in rather than derived here
 *
 * It is derived from the signatures on the server, by the same function that
 * enforces the order when a signature is submitted. A component that worked it
 * out again from props would be a second implementation of the rule, and the
 * two would disagree the first time either changed.
 */
/**
 * What to call the other side on the sequence.
 *
 * A record rather than a ternary. The ternary read fine with two parties and
 * silently called a landlord a customer the moment a third arrived — the same
 * trap the domain's COUNTERPARTY_LABEL was rewritten to close.
 */
const COUNTERPARTY_HEADING: Record<'employee' | 'customer' | 'landlord', string> = {
  employee: 'The employee',
  customer: 'The customer',
  landlord: 'The landlord',
};

export function SigningSequence({
  signed,
  turn,
  counterparty = 'employee',
  signatures = [],
}: {
  /** Parties that have signed, in order. */
  signed: readonly SigningParty[];
  /** Whose turn it is now, or null when finished. */
  turn: SigningParty | null;
  counterparty?: 'employee' | 'customer' | 'landlord';
  /** Names and times, so a signed step can say who and when. */
  signatures?: ReadonlyArray<{
    party: SigningParty;
    signerName: string;
    signerTitle: string | null;
    signedAt: string;
  }>;
}) {
  const steps: Array<{ party: SigningParty; label: string; icon: typeof Building2 }> = [
    { party: 'company', label: 'The company', icon: Building2 },
    {
      party: 'counterparty',
      label: COUNTERPARTY_HEADING[counterparty],
      icon: User,
    },
  ];

  const when = (iso: string): string =>
    new Intl.DateTimeFormat('en-KE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Africa/Nairobi',
    }).format(new Date(iso));

  return (
    <ol className="space-y-2" aria-label="Signing order">
      {steps.map((step, index) => {
        const done = signed.includes(step.party);
        const active = turn === step.party;
        const signature = signatures.find((one) => one.party === step.party);
        const Icon = step.icon;

        return (
          <li
            key={step.party}
            className={`flex items-start gap-3 rounded-xl border p-3 ${
              done
                ? 'border-secondary-300 bg-secondary-50/50'
                : active
                  ? 'border-primary-500 bg-primary-50/50'
                  : 'border-border bg-muted/20'
            }`}
          >
            <span
              className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                done
                  ? 'bg-secondary-600 text-white'
                  : active
                    ? 'bg-primary-500 text-white'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              {done ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : active ? (
                <CircleDot className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Icon className="h-4 w-4" aria-hidden="true" />
              )}
            </span>

            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium">
                  {index + 1}. {step.label}
                </span>
                {done && (
                  <span className="text-xs text-secondary-700">signed</span>
                )}
                {active && !done && (
                  <span className="text-xs font-medium text-primary-700">signing now</span>
                )}
                {/*
                  A step that is neither done nor active is waiting on the one
                  above it. Saying so is what stops somebody thinking the page
                  has failed to draw a button.
                */}
                {!done && !active && (
                  <span className="text-xs text-muted-foreground">
                    waiting for {steps[index - 1]?.label.toLowerCase() ?? 'the step above'}
                  </span>
                )}
              </span>

              {signature ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {signature.signerName}
                  {signature.signerTitle ? `, ${signature.signerTitle}` : ''} · {when(signature.signedAt)}
                </span>
              ) : active ? (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {step.party === 'company'
                    ? 'Somebody with authority signs on the business’s behalf. Nothing reaches the other side until they have.'
                    : 'The company has signed. The document is locked, so what you read is what was signed.'}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}

      {turn === null && (
        <li className="flex items-center gap-2 rounded-xl border border-secondary-300 bg-secondary-50 p-3 text-sm text-secondary-900">
          <BadgeCheck className="h-4 w-4 shrink-0 text-secondary-600" aria-hidden="true" />
          Signed by both sides. Neither can change it now.
        </li>
      )}
    </ol>
  );
}

/**
 * The boxes one party has to fill that the document has no room for.
 *
 * Only theirs, and that is deliberate rather than tidy: an employee shown the
 * salary box would reasonably try to type in it, and the company cannot be
 * blocked waiting for somebody's KRA PIN. The other side's answers are shown
 * as read-only text elsewhere on the page, so nothing is hidden — it just is
 * not editable by the wrong person.
 *
 * ## What is left here, now that filling happens in the document
 *
 * Everything that *is* a gap in the text is typed into the text. What stays
 * here is the handful of answers the wording does not contain a place for —
 * a choice between three fee arrangements whose result is one sentence, the
 * exclusivity grant, the term. See `placeholdersIn` for why the split is
 * derived from the wording rather than listed anywhere.
 *
 * Each box carries the same `data-gap` mark as an inline one, so the bar that
 * counts what is outstanding can walk somebody from a clause into this form
 * and back without either side knowing the other exists.
 */
export function SigningFields({
  fields,
  values,
  party,
  disabled = false,
  onChange,
}: {
  fields: ReadonlyArray<{
    key: string;
    label: string;
    kind: string;
    party: SigningParty;
    required: boolean;
    hint?: string;
    /** Present on a `choice` field; rendered as a dropdown. */
    options?: ReadonlyArray<{ value: string; label: string }>;
  }>;
  values: Record<string, unknown>;
  party: SigningParty;
  disabled?: boolean;
  onChange: (key: string, value: string) => void;
}) {
  const theirs = fields.filter((field) => field.party === party);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {theirs.map((field) => {
        const value = values[field.key];
        const text = value === undefined || value === null ? '' : String(value);
        const empty = field.required && text.trim().length === 0;

        /*
         * A signature box is wider and typed, not drawn.
         *
         * Kept as the typed string rather than a canvas scribble: it is what
         * the person actually did, it is searchable, it survives, and it cannot
         * fail to render in five years. The script face is what makes it read
         * as a signature rather than as another text box.
         */
        const isSignature = field.kind === 'signature' || field.kind === 'initials';

        return (
          <label
            key={field.key}
            className={`block text-sm ${isSignature ? 'sm:col-span-2' : ''}`}
          >
            <span className="mb-1 flex items-baseline gap-1.5">
              <span className="font-medium">{field.label}</span>
              {field.required && (
                <span
                  className={`text-xs ${empty ? 'text-accent-700' : 'text-muted-foreground'}`}
                >
                  {empty ? 'still empty' : 'required'}
                </span>
              )}
            </span>

            {/*
              * A fixed set of answers is a list to pick from.
              *
              * These were text boxes with the permitted values written in the
              * hint underneath — "One of: free, one_time, other" — which asks
              * somebody to type an identifier exactly right and renders an
              * empty clause in the contract when they do not.
              *
              * No blank option. The first choice is what an untouched field
              * has always meant, so a document filled in by somebody who never
              * opened the control still says something true.
              */}
            {field.kind === 'choice' && field.options ? (
              <select
                data-gap={field.key}
                className={`input w-full ${empty ? 'border-accent-400' : ''}`}
                value={text || field.options[0]?.value || ''}
                disabled={disabled}
                onChange={(event) => onChange(field.key, event.target.value)}
                aria-invalid={empty}
              >
                {field.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                data-gap={field.key}
                type={field.kind === 'date' ? 'date' : field.kind === 'money' ? 'number' : 'text'}
                inputMode={field.kind === 'money' ? 'numeric' : undefined}
                className={`input w-full ${
                  isSignature ? 'signature-ink text-2xl' : ''
                } ${empty ? 'border-accent-400' : ''}`}
                value={text}
                disabled={disabled}
                onChange={(event) => onChange(field.key, event.target.value)}
                placeholder={isSignature ? 'Type your full name' : undefined}
                aria-invalid={empty}
              />
            )}

            {field.hint && (
              <span className="mt-1 block text-xs text-muted-foreground">{field.hint}</span>
            )}
          </label>
        );
      })}
    </div>
  );
}
